import { useRef, useState } from 'react';

import type { CreditParameterSpec, CreditRuleSpec } from '@/shared/api/resources';
import { BRL } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';

import {
  ehDinheiro,
  invalido,
  lerDinheiro,
  lerInteiro,
  lerJson,
  rotuloDaClassificacao,
  rotuloDe,
  textoDaDisponibilidade,
  textoDoDinheiro,
  tomDaClassificacao,
  valido,
  type Resultado,
} from './valores';

/**
 * Editores dos parametros, guiados pelo tipo do catalogo descritivo.
 *
 * Cada editor nasce do valor gravado no rascunho e devolve, a cada mudanca, o valor
 * pronto para o servidor ou o erro a mostrar. Nenhum editor transforma vazio em zero:
 * o parametro que aceita "em branco" tem a caixa propria, e o que nao aceita recusa o
 * campo vazio.
 */
export interface PropsDoEditor {
  spec: CreditParameterSpec;
  inicial: unknown;
  rotulos: Record<string, string>;
  regras: CreditRuleSpec[];
  onChange: (resultado: Resultado) => void;
}

export function EditorDoParametro(props: PropsDoEditor) {
  const { spec } = props;
  if (spec.key === 'regras_homologadas')
    return <EditorDeRegrasHomologadas {...props} />;
  switch (spec.kind) {
    case 'booleano':
      return <CampoBooleano {...props} />;
    case 'inteiro':
      return <CampoInteiro {...props} />;
    case 'dinheiro':
      return (
        <CampoDinheiro
          label={spec.label}
          inicial={props.inicial}
          aceitaEmBranco={spec.nullable}
          onChange={props.onChange}
        />
      );
    case 'opcao':
      return <CampoOpcao {...props} />;
    case 'lista':
    case 'mapa':
    case 'tabela':
      return <ComAlternativaJson {...props} />;
    default:
      return (
        <EditorJson
          label={spec.label}
          inicial={props.inicial}
          onChange={props.onChange}
        />
      );
  }
}

// --- Campos simples ---------------------------------------------------------------

function CaixaEmBranco({
  rotulo,
  marcada,
  onChange,
}: {
  rotulo: string;
  marcada: boolean;
  onChange: (marcada: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-ink-700">
      <input
        type="checkbox"
        aria-label={`${rotulo} em branco`}
        checked={marcada}
        onChange={(event) => onChange(event.target.checked)}
      />
      Em branco
    </label>
  );
}

const pecaOValor = 'Informe o valor ou marque "Em branco".';

/**
 * Dinheiro do parametro: `MoneyField` com a caixa "Em branco" e o resultado do editor.
 *
 * A mascara e a leitura sao as do campo unico; aqui so se decide o que e branco. Reabrir
 * e salvar sem mexer devolve o mesmo valor ao servidor (SC-R21).
 */
export function CampoDinheiro({
  label,
  inicial,
  aceitaEmBranco,
  onChange,
  labelHidden,
}: {
  label: string;
  inicial: unknown;
  aceitaEmBranco: boolean;
  onChange: (resultado: Resultado) => void;
  labelHidden?: boolean;
}) {
  const dinheiro = ehDinheiro(inicial) ? inicial : null;
  const moeda = dinheiro?.currency ?? BRL;
  const [texto, setTexto] = useState(textoDoDinheiro(dinheiro));
  const [emBranco, setEmBranco] = useState(aceitaEmBranco && inicial === null);

  function ler(t: string, branco: boolean): Resultado {
    if (branco) return valido(null);
    if (t.trim() === '' && aceitaEmBranco) return invalido(pecaOValor);
    return lerDinheiro(t, false, moeda);
  }
  const resultado = ler(texto, emBranco);

  return (
    <div className="flex flex-col gap-1">
      <MoneyField
        label={label}
        labelHidden={labelHidden}
        help={false}
        currency={moeda}
        placeholder={emBranco ? 'Em branco' : undefined}
        value={emBranco ? '' : texto}
        disabled={emBranco}
        error={resultado.ok ? undefined : resultado.erro}
        onValueChange={(novo) => {
          setTexto(novo);
          onChange(ler(novo, false));
        }}
      />
      {aceitaEmBranco ? (
        <CaixaEmBranco
          rotulo={label}
          marcada={emBranco}
          onChange={(marcada) => {
            setEmBranco(marcada);
            onChange(ler(texto, marcada));
          }}
        />
      ) : null}
    </div>
  );
}

function CampoInteiro({ spec, inicial, onChange }: PropsDoEditor) {
  const [texto, setTexto] = useState(
    typeof inicial === 'number' ? String(inicial) : '',
  );
  const [emBranco, setEmBranco] = useState(spec.nullable && inicial === null);

  function ler(t: string, branco: boolean): Resultado {
    if (branco) return valido(null);
    if (t.trim() === '' && spec.nullable) return invalido(pecaOValor);
    return lerInteiro(t, false);
  }
  const resultado = ler(texto, emBranco);

  return (
    <div className="flex flex-col gap-1">
      <Field
        label={spec.label}
        help={false}
        inputMode="numeric"
        value={emBranco ? '' : texto}
        placeholder={emBranco ? 'Em branco' : undefined}
        disabled={emBranco}
        hint={spec.unit}
        error={resultado.ok ? undefined : resultado.erro}
        onChange={(event) => {
          setTexto(event.target.value);
          onChange(ler(event.target.value, false));
        }}
      />
      {spec.nullable ? (
        <CaixaEmBranco
          rotulo={spec.label}
          marcada={emBranco}
          onChange={(marcada) => {
            setEmBranco(marcada);
            onChange(ler(texto, marcada));
          }}
        />
      ) : null}
    </div>
  );
}

function CampoBooleano({ spec, inicial, onChange }: PropsDoEditor) {
  const [valor, setValor] = useState(
    inicial === true ? 'sim' : inicial === false ? 'nao' : '',
  );
  return (
    <Select
      label={spec.label}
      help={false}
      value={valor}
      placeholder={valor === '' && !spec.nullable ? 'Escolha' : undefined}
      options={[
        { value: 'sim', label: 'Sim' },
        { value: 'nao', label: 'Nao' },
        ...(spec.nullable ? [{ value: '', label: 'Em branco' }] : []),
      ]}
      onChange={(event) => {
        const escolhido = event.target.value;
        setValor(escolhido);
        onChange(
          escolhido === ''
            ? spec.nullable
              ? valido(null)
              : invalido('Escolha sim ou nao.')
            : valido(escolhido === 'sim'),
        );
      }}
    />
  );
}

function CampoOpcao({ spec, inicial, rotulos, onChange }: PropsDoEditor) {
  const opcoes = spec.options ?? [];
  const [valor, setValor] = useState(typeof inicial === 'string' ? inicial : '');
  const [emBranco, setEmBranco] = useState(spec.nullable && inicial === null);

  // Opcao sem lista e texto livre, como o nome do modelo de score.
  if (opcoes.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <Field
          label={spec.label}
          help={false}
          value={emBranco ? '' : valor}
          disabled={emBranco}
          onChange={(event) => {
            setValor(event.target.value);
            onChange(valido(event.target.value.trim()));
          }}
        />
        {spec.nullable ? (
          <CaixaEmBranco
            rotulo={spec.label}
            marcada={emBranco}
            onChange={(marcada) => {
              setEmBranco(marcada);
              onChange(marcada ? valido(null) : valido(valor.trim()));
            }}
          />
        ) : null}
      </div>
    );
  }

  // Em branco tem marcador proprio, porque o texto vazio pode ser uma opcao (o modelo
  // de score "nao definido"). O valor gravado fora da lista continua oferecido:
  // reabrir e salvar nunca troca o que estava gravado.
  const lista = [...opcoes];
  if (typeof inicial === 'string' && !lista.includes(inicial)) lista.push(inicial);
  const semEscolha = !spec.nullable && inicial === null;

  return (
    <Select
      label={spec.label}
      help={false}
      value={
        emBranco ? MARCA_EM_BRANCO : semEscolha && valor === '' ? MARCA_ESCOLHA : valor
      }
      options={[
        ...(spec.nullable ? [{ value: MARCA_EM_BRANCO, label: 'Em branco' }] : []),
        ...(semEscolha ? [{ value: MARCA_ESCOLHA, label: 'Escolha' }] : []),
        ...lista.map((opcao) => ({
          value: opcao,
          label: rotuloDaOpcao(opcao, rotulos),
        })),
      ]}
      onChange={(event) => {
        const escolhido = event.target.value;
        if (escolhido === MARCA_EM_BRANCO) {
          setEmBranco(true);
          onChange(valido(null));
          return;
        }
        setEmBranco(false);
        setValor(escolhido);
        onChange(
          escolhido === MARCA_ESCOLHA
            ? invalido('Escolha uma opcao.')
            : valido(escolhido),
        );
      }}
    />
  );
}

// Marcadores fora do vocabulario das opcoes (minusculas e sublinhado simples).
const MARCA_EM_BRANCO = '__em_branco__';
const MARCA_ESCOLHA = '__escolha__';

function rotuloDaOpcao(opcao: string, rotulos: Record<string, string>): string {
  return opcao === '' ? 'Nao definido' : rotuloDe(opcao, rotulos);
}

// --- Estruturas -------------------------------------------------------------------

/** Editor da estrutura em JSON, validado: todo numero e inteiro exato. */
export function EditorJson({
  label,
  inicial,
  onChange,
}: {
  label: string;
  inicial: unknown;
  onChange: (resultado: Resultado) => void;
}) {
  const [texto, setTexto] = useState(() => JSON.stringify(inicial ?? null, null, 2));
  const resultado = lerJson(texto);
  return (
    <Textarea
      label={label}
      help={false}
      value={texto}
      rows={Math.min(16, Math.max(3, texto.split('\n').length))}
      className="font-mono text-xs"
      spellCheck={false}
      hint="Estrutura em JSON. Numeros inteiros: dinheiro em centavos, fator em milesimos."
      error={resultado.ok ? undefined : resultado.erro}
      onChange={(event) => {
        setTexto(event.target.value);
        onChange(lerJson(event.target.value));
      }}
    />
  );
}

/**
 * Lista, mapa e tabela tem o editor proprio e a alternativa de editar em JSON, para o
 * que o editor proprio nao cobre (chave nova num mapa de mapas, por exemplo).
 */
function ComAlternativaJson(props: PropsDoEditor) {
  const { spec, onChange } = props;
  const [modo, setModo] = useState<'proprio' | 'json'>(() =>
    editorProprioCabe(spec, props.inicial) ? 'proprio' : 'json',
  );
  // O ultimo valor valido, para a troca de editor comecar de onde a pessoa parou.
  const [ultimo, setUltimo] = useState<unknown>(props.inicial);
  const [geracao, setGeracao] = useState(0);
  const cabe = editorProprioCabe(spec, ultimo);

  function mudar(resultado: Resultado) {
    if (resultado.ok) setUltimo(resultado.valor);
    onChange(resultado);
  }

  return (
    <div className="flex flex-col gap-2">
      {modo === 'json' || !cabe ? (
        <EditorJson
          key={`json-${geracao}`}
          label={spec.label}
          inicial={ultimo}
          onChange={mudar}
        />
      ) : spec.kind === 'lista' ? (
        <EditorDeLista key={geracao} {...props} inicial={ultimo} onChange={mudar} />
      ) : spec.kind === 'mapa' ? (
        <EditorDeMapa key={geracao} {...props} inicial={ultimo} onChange={mudar} />
      ) : (
        <EditorDeTabela key={geracao} {...props} inicial={ultimo} onChange={mudar} />
      )}
      {cabe ? (
        <div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setModo(modo === 'json' ? 'proprio' : 'json');
              setGeracao((g) => g + 1);
            }}
          >
            {modo === 'json' ? 'Voltar ao editor' : `Editar ${spec.label} em JSON`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function objetoSimples(valor: unknown): valor is Record<string, unknown> {
  return valor !== null && typeof valor === 'object' && !Array.isArray(valor);
}

type TipoDoMapa = 'opcao' | 'booleano' | 'dinheiro' | 'matriz';

function tipoDoMapa(spec: CreditParameterSpec, valor: unknown): TipoDoMapa | null {
  if (!objetoSimples(valor)) return null;
  if ((spec.options ?? []).length > 0) return 'opcao';
  const valores = Object.values(valor);
  if (valores.length === 0) return null;
  if (valores.every((v) => typeof v === 'boolean')) return 'booleano';
  if (valores.every((v) => v === null || ehDinheiro(v)) && valores.some(ehDinheiro)) {
    return 'dinheiro';
  }
  if (
    valores.every(
      (v) => objetoSimples(v) && Object.values(v).every((c) => typeof c === 'boolean'),
    )
  ) {
    return 'matriz';
  }
  return null;
}

type TipoDaColuna = 'dinheiro' | 'inteiro' | 'booleano' | 'texto';

function colunasDaTabela(
  valor: unknown,
): { nome: string; tipo: TipoDaColuna }[] | null {
  if (!Array.isArray(valor) || valor.length === 0) return null;
  if (!valor.every(objetoSimples)) return null;
  const linhas = valor;
  const nomes = [...new Set(linhas.flatMap((linha) => Object.keys(linha)))];
  const colunas: { nome: string; tipo: TipoDaColuna }[] = [];
  for (const nome of nomes) {
    const celulas = linhas.map((linha) => linha[nome]).filter((c) => c !== null);
    if (
      celulas.some((c) => objetoSimples(c) && !ehDinheiro(c)) ||
      celulas.some(Array.isArray)
    ) {
      return null;
    }
    const tipo: TipoDaColuna = celulas.some(ehDinheiro)
      ? 'dinheiro'
      : celulas.some((c) => typeof c === 'number')
        ? 'inteiro'
        : celulas.some((c) => typeof c === 'boolean')
          ? 'booleano'
          : 'texto';
    colunas.push({ nome, tipo });
  }
  return colunas;
}

function editorProprioCabe(spec: CreditParameterSpec, valor: unknown): boolean {
  if (spec.kind === 'lista') {
    return Array.isArray(valor) && valor.every((item) => typeof item === 'string');
  }
  if (spec.kind === 'mapa') return tipoDoMapa(spec, valor) !== null;
  if (spec.kind === 'tabela') return colunasDaTabela(valor) !== null;
  return false;
}

function EditorDeLista({ spec, inicial, rotulos, onChange }: PropsDoEditor) {
  const opcoes = spec.options ?? [];
  const [itens, setItens] = useState<string[]>(
    Array.isArray(inicial)
      ? inicial.filter((i): i is string => typeof i === 'string')
      : [],
  );
  const [texto, setTexto] = useState(itens.join('\n'));

  if (opcoes.length === 0) {
    return (
      <Textarea
        label={spec.label}
        help={false}
        value={texto}
        rows={Math.min(10, Math.max(3, itens.length + 1))}
        hint={`Um item por linha${spec.unit ? ` (${spec.unit})` : ''}. Linha vazia e ignorada.`}
        onChange={(event) => {
          setTexto(event.target.value);
          const novos = event.target.value
            .split('\n')
            .map((linha) => linha.trim())
            .filter((linha) => linha !== '');
          setItens(novos);
          onChange(valido(novos));
        }}
      />
    );
  }

  function mudar(novos: string[]) {
    setItens(novos);
    onChange(valido(novos));
  }
  function mover(indice: number, passo: number) {
    const destino = indice + passo;
    if (destino < 0 || destino >= itens.length) return;
    const novos = [...itens];
    const [item] = novos.splice(indice, 1);
    novos.splice(destino, 0, item as string);
    mudar(novos);
  }
  const restantes = opcoes.filter((opcao) => !itens.includes(opcao));

  return (
    <div className="flex flex-col gap-2">
      {itens.length === 0 ? (
        <p className="text-xs text-ink-500 italic">Nenhum item na lista.</p>
      ) : (
        <ol aria-label={spec.label} className="flex flex-col gap-1">
          {itens.map((item, indice) => {
            const nome = rotuloDe(item, rotulos);
            return (
              <li
                key={item}
                className="flex items-center gap-2 rounded-md border border-line px-2 py-1 text-xs"
              >
                <span className="w-5 text-ink-500">{indice + 1}.</span>
                <span className="flex-1 text-ink-900">{nome}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Subir ${nome}`}
                  disabled={indice === 0}
                  onClick={() => mover(indice, -1)}
                >
                  Subir
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Descer ${nome}`}
                  disabled={indice === itens.length - 1}
                  onClick={() => mover(indice, 1)}
                >
                  Descer
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Retirar ${nome}`}
                  onClick={() => mudar(itens.filter((i) => i !== item))}
                >
                  Retirar
                </Button>
              </li>
            );
          })}
        </ol>
      )}
      {restantes.length > 0 ? (
        <Select
          label={`Acrescentar em ${spec.label}`}
          help={false}
          value=""
          placeholder="Escolha para acrescentar"
          options={restantes.map((opcao) => ({
            value: opcao,
            label: rotuloDe(opcao, rotulos),
          }))}
          onChange={(event) => {
            if (event.target.value) mudar([...itens, event.target.value]);
          }}
        />
      ) : null}
    </div>
  );
}

function combinarMapa(
  resultados: Record<string, Resultado>,
  rotulos: Record<string, string>,
): Resultado {
  const saida: Record<string, unknown> = {};
  for (const [chave, resultado] of Object.entries(resultados)) {
    if (!resultado.ok)
      return invalido(`${rotuloDe(chave, rotulos)}: ${resultado.erro}`);
    saida[chave] = resultado.valor;
  }
  return valido(saida);
}

function EditorDeMapa(props: PropsDoEditor) {
  const tipo = tipoDoMapa(props.spec, props.inicial);
  const objeto = objetoSimples(props.inicial) ? props.inicial : {};
  if (tipo === 'dinheiro') return <MapaDeDinheiro {...props} objeto={objeto} />;
  if (tipo === 'booleano') return <MapaDeBooleanos {...props} objeto={objeto} />;
  if (tipo === 'matriz') return <MapaMatriz {...props} objeto={objeto} />;
  return <MapaDeOpcoes {...props} objeto={objeto} />;
}

function MapaDeDinheiro({
  spec,
  objeto,
  rotulos,
  onChange,
}: PropsDoEditor & { objeto: Record<string, unknown> }) {
  const resultados = useRef<Record<string, Resultado>>(
    Object.fromEntries(Object.entries(objeto).map(([chave, v]) => [chave, valido(v)])),
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Object.keys(objeto).map((chave) => (
        <CampoDinheiro
          key={chave}
          label={rotuloDe(chave, rotulos)}
          inicial={objeto[chave]}
          aceitaEmBranco={spec.nullable}
          onChange={(resultado) => {
            resultados.current = { ...resultados.current, [chave]: resultado };
            onChange(combinarMapa(resultados.current, rotulos));
          }}
        />
      ))}
    </div>
  );
}

function MapaDeBooleanos({
  objeto,
  rotulos,
  onChange,
}: PropsDoEditor & { objeto: Record<string, unknown> }) {
  const [valores, setValores] = useState(objeto as Record<string, boolean>);
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      {Object.keys(valores).map((chave) => (
        <label key={chave} className="flex items-center gap-2 text-xs text-ink-800">
          <input
            type="checkbox"
            checked={valores[chave] === true}
            onChange={(event) => {
              const novos = { ...valores, [chave]: event.target.checked };
              setValores(novos);
              onChange(valido(novos));
            }}
          />
          {rotuloDe(chave, rotulos)}
        </label>
      ))}
    </div>
  );
}

function MapaMatriz({
  objeto,
  rotulos,
  onChange,
}: PropsDoEditor & { objeto: Record<string, unknown> }) {
  const [valores, setValores] = useState(
    objeto as Record<string, Record<string, boolean>>,
  );
  const linhas = Object.keys(valores);
  const colunas = [
    ...new Set(linhas.flatMap((linha) => Object.keys(valores[linha] ?? {}))),
  ];
  return (
    <div className="overflow-x-auto">
      <table className="text-xs">
        <thead>
          <tr>
            <th />
            {colunas.map((coluna) => (
              <th key={coluna} className="px-2 text-left font-semibold text-ink-600">
                {rotuloDe(coluna, rotulos)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr key={linha}>
              <th className="pr-2 text-left font-semibold text-ink-600">
                {rotuloDe(linha, rotulos)}
              </th>
              {colunas.map((coluna) => (
                <td key={coluna} className="px-2">
                  <input
                    type="checkbox"
                    aria-label={`${rotuloDe(linha, rotulos)} em ${rotuloDe(coluna, rotulos)}`}
                    checked={valores[linha]?.[coluna] === true}
                    onChange={(event) => {
                      const novos = {
                        ...valores,
                        [linha]: { ...valores[linha], [coluna]: event.target.checked },
                      };
                      setValores(novos);
                      onChange(valido(novos));
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface LinhaDoMapa {
  id: number;
  chave: string;
  valor: string;
}

/**
 * Quantas linhas do mapa aparecem de uma vez. A classificacao dos CNAEs tem mais de mil:
 * desenhar todas travaria a tela, e ninguem edita procurando no meio delas.
 */
const LINHAS_VISIVEIS = 40;

/** Mapa de chave livre para uma das opcoes, como a classificacao dos CNAEs. */
function MapaDeOpcoes({
  spec,
  objeto,
  rotulos,
  onChange,
}: PropsDoEditor & { objeto: Record<string, unknown> }) {
  const opcoes = spec.options ?? [];
  const [linhas, setLinhas] = useState<LinhaDoMapa[]>(() =>
    Object.entries(objeto).map(([chave, valor], indice) => ({
      id: indice,
      chave,
      valor: typeof valor === 'string' ? valor : '',
    })),
  );
  // Identificador da proxima linha: so cresce, para a linha nova nunca repetir o de
  // uma retirada.
  const proximo = useRef(Object.keys(objeto).length);
  const [busca, setBusca] = useState('');
  const grande = linhas.length > LINHAS_VISIVEIS;
  const termo = busca.trim().toLowerCase();
  const encontradas = termo
    ? linhas.filter(
        (linha) =>
          linha.chave.toLowerCase().includes(termo) ||
          rotuloDe(linha.valor, rotulos).toLowerCase().includes(termo),
      )
    : linhas;
  const visiveis = grande ? encontradas.slice(0, LINHAS_VISIVEIS) : encontradas;

  function mudar(novas: LinhaDoMapa[]) {
    setLinhas(novas);
    const saida: Record<string, string> = {};
    for (const linha of novas) {
      const chave = linha.chave.trim();
      if (chave === '') {
        onChange(invalido('Preencha a chave de cada linha.'));
        return;
      }
      if (chave in saida) {
        onChange(invalido(`Chave repetida: ${chave}.`));
        return;
      }
      if (!opcoes.includes(linha.valor)) {
        onChange(invalido(`Escolha a classificacao de ${chave}.`));
        return;
      }
      saida[chave] = linha.valor;
    }
    onChange(valido(saida));
  }

  return (
    <div className="flex flex-col gap-2">
      {linhas.length === 0 ? (
        <p className="text-xs text-ink-500 italic">Nenhuma linha.</p>
      ) : null}
      {grande ? (
        <div className="flex flex-col gap-1">
          <Field
            label={`Buscar em ${spec.label}`}
            help={false}
            value={busca}
            placeholder="Codigo ou classificacao"
            onChange={(event) => setBusca(event.target.value)}
          />
          <p className="text-2xs text-ink-500">
            {`Mostrando ${visiveis.length.toLocaleString('pt-BR')} de ${encontradas.length.toLocaleString('pt-BR')}${
              termo ? ' encontrados' : ' linhas'
            }. A busca nao muda nada: todas as linhas continuam na versao.`}
          </p>
        </div>
      ) : null}
      {visiveis.map((linha, indice) => (
        <div key={linha.id} className="flex items-end gap-2">
          <div className="flex-1">
            <Field
              label={`Chave da linha ${indice + 1}`}
              help={false}
              value={linha.chave}
              onChange={(event) =>
                mudar(
                  linhas.map((l) =>
                    l.id === linha.id ? { ...l, chave: event.target.value } : l,
                  ),
                )
              }
            />
          </div>
          <div className="flex-1">
            <Select
              label={`Valor da linha ${indice + 1}`}
              help={false}
              value={linha.valor}
              placeholder="Escolha"
              options={opcoes.map((opcao) => ({
                value: opcao,
                label: rotuloDe(opcao, rotulos),
              }))}
              onChange={(event) =>
                mudar(
                  linhas.map((l) =>
                    l.id === linha.id ? { ...l, valor: event.target.value } : l,
                  ),
                )
              }
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={`Retirar a linha ${indice + 1}`}
            onClick={() => mudar(linhas.filter((l) => l.id !== linha.id))}
          >
            Retirar
          </Button>
        </div>
      ))}
      <div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            const nova = { id: proximo.current++, chave: '', valor: '' };
            // Na lista grande, a linha nova vem primeiro: no fim ficaria fora da vista.
            setBusca('');
            mudar(grande ? [nova, ...linhas] : [...linhas, nova]);
          }}
        >
          Acrescentar linha
        </Button>
      </div>
    </div>
  );
}

interface LinhaDaTabela {
  id: number;
  inicial: Record<string, unknown>;
  celulas: Record<string, Resultado>;
}

/** Tabela de faixas ou de linhas, com o tipo de cada coluna lido dos valores. */
function EditorDeTabela({ spec, inicial, rotulos, onChange }: PropsDoEditor) {
  const colunas = colunasDaTabela(inicial) ?? [];
  const [linhas, setLinhas] = useState<LinhaDaTabela[]>(() =>
    (inicial as Record<string, unknown>[]).map((linha, indice) => ({
      id: indice,
      inicial: linha,
      celulas: Object.fromEntries(
        colunas.map((c) => [c.nome, valido(linha[c.nome] ?? null)]),
      ),
    })),
  );
  const proximo = useRef(Array.isArray(inicial) ? inicial.length : 0);
  // As celulas guardam o proprio texto; aqui fica o resultado de cada uma, para
  // montar a tabela inteira a cada mudanca.
  const atuais = useRef(linhas);

  function emitir(novas: LinhaDaTabela[]) {
    atuais.current = novas;
    const saida: Record<string, unknown>[] = [];
    for (const [indice, linha] of novas.entries()) {
      const registro: Record<string, unknown> = {};
      for (const coluna of colunas) {
        const celula = linha.celulas[coluna.nome];
        if (!celula || !celula.ok) {
          onChange(
            invalido(
              `Linha ${indice + 1}, ${rotuloDe(coluna.nome, rotulos)}: ${celula && !celula.ok ? celula.erro : 'preencha.'}`,
            ),
          );
          return;
        }
        registro[coluna.nome] = celula.valor;
      }
      saida.push(registro);
    }
    onChange(valido(saida));
  }

  function mudarCelula(id: number, coluna: string, resultado: Resultado) {
    const novas = atuais.current.map((linha) =>
      linha.id === id
        ? { ...linha, celulas: { ...linha.celulas, [coluna]: resultado } }
        : linha,
    );
    emitir(novas);
  }

  function linhaNova(): LinhaDaTabela {
    const celulas: Record<string, Resultado> = {};
    const vazia: Record<string, unknown> = {};
    for (const coluna of colunas) {
      if (coluna.tipo === 'dinheiro') {
        celulas[coluna.nome] = spec.nullable
          ? invalido(pecaOValor)
          : invalido('Informe o valor.');
      } else if (coluna.tipo === 'inteiro') {
        celulas[coluna.nome] = invalido('Informe um numero inteiro.');
      } else if (coluna.tipo === 'booleano') {
        celulas[coluna.nome] = valido(false);
        vazia[coluna.nome] = false;
      } else {
        celulas[coluna.nome] = valido('');
        vazia[coluna.nome] = '';
      }
    }
    return { id: proximo.current++, inicial: vazia, celulas };
  }

  return (
    <div className="flex flex-col gap-2 overflow-x-auto">
      <table className="text-xs">
        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th
                key={coluna.nome}
                className="px-1 text-left font-semibold text-ink-600"
              >
                {rotuloDe(coluna.nome, rotulos)}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha, indice) => (
            <tr key={linha.id} className="align-top">
              {colunas.map((coluna) => {
                const rotulo = `${rotuloDe(coluna.nome, rotulos)}, linha ${indice + 1}`;
                return (
                  <td key={coluna.nome} className="px-1 py-0.5">
                    <CelulaDaTabela
                      tipo={coluna.tipo}
                      rotulo={rotulo}
                      inicial={linha.inicial[coluna.nome]}
                      aceitaEmBranco={spec.nullable}
                      onChange={(resultado) =>
                        mudarCelula(linha.id, coluna.nome, resultado)
                      }
                    />
                  </td>
                );
              })}
              <td className="px-1 py-0.5">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  aria-label={`Retirar a linha ${indice + 1}`}
                  onClick={() => {
                    const novas = atuais.current.filter((l) => l.id !== linha.id);
                    setLinhas(novas);
                    emitir(novas);
                  }}
                >
                  Retirar
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            const novas = [...atuais.current, linhaNova()];
            setLinhas(novas);
            emitir(novas);
          }}
        >
          Acrescentar linha
        </Button>
      </div>
    </div>
  );
}

function CelulaDaTabela({
  tipo,
  rotulo,
  inicial,
  aceitaEmBranco,
  onChange,
}: {
  tipo: TipoDaColuna;
  rotulo: string;
  inicial: unknown;
  aceitaEmBranco: boolean;
  onChange: (resultado: Resultado) => void;
}) {
  const [texto, setTexto] = useState(
    typeof inicial === 'number' || typeof inicial === 'string' ? String(inicial) : '',
  );
  const [marcado, setMarcado] = useState(inicial === true);

  if (tipo === 'dinheiro') {
    return (
      <CampoDinheiro
        label={rotulo}
        labelHidden
        inicial={inicial}
        aceitaEmBranco={aceitaEmBranco}
        onChange={onChange}
      />
    );
  }
  if (tipo === 'booleano') {
    return (
      <input
        type="checkbox"
        aria-label={rotulo}
        checked={marcado}
        onChange={(event) => {
          setMarcado(event.target.checked);
          onChange(valido(event.target.checked));
        }}
      />
    );
  }
  const resultado =
    tipo === 'inteiro' ? lerInteiro(texto, aceitaEmBranco) : valido(texto);
  return (
    <Field
      label={rotulo}
      labelHidden
      help={false}
      value={texto}
      inputMode={tipo === 'inteiro' ? 'numeric' : undefined}
      error={resultado.ok ? undefined : resultado.erro}
      onChange={(event) => {
        setTexto(event.target.value);
        onChange(
          tipo === 'inteiro'
            ? lerInteiro(event.target.value, aceitaEmBranco)
            : valido(event.target.value.trim()),
        );
      }}
    />
  );
}

// --- Regras homologadas -------------------------------------------------------------

/**
 * Lista de regras homologadas: so as regras com avaliacao propria, com o nome e a
 * classificacao de cada uma. Fora da lista, a regra roda em sombra.
 */
function EditorDeRegrasHomologadas({ inicial, regras, onChange }: PropsDoEditor) {
  const avaliadas = regras.filter((regra) => regra.evaluated);
  const [selecionadas, setSelecionadas] = useState<string[]>(
    Array.isArray(inicial)
      ? inicial.filter((i): i is string => typeof i === 'string')
      : [],
  );

  function alternar(id: string, marcada: boolean) {
    const conjunto = new Set(selecionadas);
    if (marcada) conjunto.add(id);
    else conjunto.delete(id);
    // A ordem e a do catalogo; regra fora do catalogo (de outra versao do motor)
    // continua na lista ate alguem tira-la.
    const ordenadas = [
      ...avaliadas.filter((regra) => conjunto.has(regra.id)).map((regra) => regra.id),
      ...[...conjunto].filter((rid) => !avaliadas.some((regra) => regra.id === rid)),
    ];
    setSelecionadas(ordenadas);
    onChange(valido(ordenadas));
  }

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="sr-only">Regras homologadas</legend>
      <Alert tone="warning" title="Regra fora da lista roda em sombra">
        A regra que nao esta marcada registra o que teria decidido e encaminha para
        analise manual, sem decidir (PM-01). So decide a regra marcada numa versao
        ativa. Homologar e decisao de quem tem alcada, depois da validacao da regra.
      </Alert>
      <ul className="flex flex-col gap-1">
        {avaliadas.map((regra) => (
          <li key={regra.id} className="flex flex-wrap items-center gap-2 text-xs">
            <label className="flex items-center gap-2 text-ink-900">
              <input
                type="checkbox"
                checked={selecionadas.includes(regra.id)}
                onChange={(event) => alternar(regra.id, event.target.checked)}
              />
              <span className="font-mono">{regra.id}</span> {regra.name}
            </label>
            <Badge tone={tomDaClassificacao(regra.classification)}>
              {rotuloDaClassificacao(regra.classification)}
            </Badge>
            <span className="text-ink-500">
              {textoDaDisponibilidade(regra.availability)}
            </span>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}
